import React, { useState } from "react";
import { Dialog } from "components/dialog";
import { FeedbackTypeEnum } from "types";
import { useTranslation } from "react-i18next";

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: string) => void;
  feedbackType: FeedbackTypeEnum;
  messageId: string;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  feedbackType,
  messageId,
}) => {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(feedback);
      setFeedback("");
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedback("");
    onClose();
  };

  const isLiked = feedbackType === FeedbackTypeEnum.CONTRIBUTE;
  const title = isLiked
    ? t("chat.feedbackDialog.likedTitle")
    : t("chat.feedbackDialog.improvedTitle");
  const subtitle = t("chat.feedbackDialog.subtitle");
  const placeholder = isLiked
    ? t("chat.feedbackDialog.likedPlaceholder")
    : t("chat.feedbackDialog.improvedPlaceholder");

  return (
    <Dialog
      isOpen={isOpen}
      setIsOpen={() => {}} // Controlled by parent
      onClose={handleClose}
      header={title}
      subHeader={subtitle}
      className="w-full max-w-md"
    >
      <div className="space-y-4">
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("chat.feedbackDialog.feedbackLabel")}
              <span className="ml-1 text-red-500 dark:text-red-400">*</span>
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={placeholder}
              required
              rows={4}
              className="w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm transition-colors focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-purple-400 dark:focus:ring-purple-400"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={!feedback.trim() || isSubmitting}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                isLiked
                  ? "bg-green-600 hover:bg-green-700 disabled:bg-green-400"
                  : "bg-red-600 hover:bg-red-700 disabled:bg-red-400"
              }`}
            >
              {isSubmitting
                ? t("chat.feedbackDialog.submitting")
                : t("chat.feedbackDialog.submitFeedback")}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};
